import React, { useMemo } from 'react';

import { Flag, TagIcon, UserRoundIcon } from 'lucide-react';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import type { WorkflowQuickFilterItem } from 'src/components/workflow/workflow-list/WorkflowQuickFilters';
import { WorkflowQuickFilters } from 'src/components/workflow/workflow-list/WorkflowQuickFilters';
import type { ReactSetStateTuple } from 'src/types';

import { BoardFilterOption } from './BoardFilterOption';

interface BoardFilterConfig {
  field: 'labels' | 'permissions' | 'priority';
  id: string;
  label: string;
  type: 'labels' | 'assignee' | 'priority';
  icon?: React.ReactNode;
}

interface BoardFiltersProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
}

const FILTERS: BoardFilterConfig[] = [
  {
    field: 'priority',
    icon: <Flag size={16} />,
    id: 'priority',
    label: 'Priority',
    type: 'priority',
  },
  {
    field: 'labels',
    icon: <TagIcon height={16} width={16} />,
    id: 'labels',
    label: 'Labels',
    type: 'labels',
  },
  {
    field: 'permissions',
    icon: <UserRoundIcon height={16} width={16} />,
    id: 'assignee',
    label: 'Assignee',
    type: 'assignee',
  },
];

function getActiveFilterCount(
  boardState: BoardLocalState,
  field: BoardFilterConfig['field']
): number {
  switch (field) {
    case 'labels':
      return boardState.filterLabels.length;
    case 'permissions':
      return boardState.filterAssignees.length;
    case 'priority':
      return boardState.filterPriorities?.length ?? 0;
  }
}

export function BoardFilters({ boardStateTuple }: BoardFiltersProps): JSX.Element {
  const [boardState] = boardStateTuple;
  const items: WorkflowQuickFilterItem[] = useMemo(
    () =>
      FILTERS.map(filter => ({
        activeCount: getActiveFilterCount(boardState, filter.field),
        id: filter.id,
        render: () => <BoardFilterOption boardStateTuple={boardStateTuple} filter={filter} />,
      })),
    [boardState, boardStateTuple]
  );

  return <WorkflowQuickFilters items={items} />;
}
